import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Counting } from '@core/models/counting.model';
import { ImageCounts } from '@core/models/image-counts.model';
import { Point } from '@core/models/point.model';
import { environment } from '@environment/environment';
import { BehaviorSubject, map, Observable } from 'rxjs';
@Injectable()
export class CountingService {
  imageCounts = new Map();

  defaultColorSequence = [
    '#2E0E02',
    '#581908',
    '#983628',
    '#E2AEDD',
    '#EBCBF4',
  ];

  countingsUpdated = new BehaviorSubject<{
    imageId: number;
    counts: ImageCounts | null;
  }>({ imageId: -1, counts: null });
  constructor(private httpClient: HttpClient) {}

  createCountingForImage(imageId: number) {
    if (!this.imageCounts.has(imageId)) {
      this.imageCounts.set(imageId, new ImageCounts());
    }
    const numCounts = this.imageCounts.get(imageId).countings.length;
    const currentColor =
      this.defaultColorSequence[numCounts % this.defaultColorSequence.length];
    const newCounting = new Counting();
    newCounting.color = currentColor;
    newCounting.active = true;
    this.imageCounts
      .get(imageId)
      .countings.forEach((c: any) => (c.active = false));
    this.imageCounts.get(imageId).countings.push(newCounting);
    return this.imageCounts.get(imageId);
  }

  clearCountings(imageId: number) {
    if (!this.imageCounts.has(imageId)) return;
    this.imageCounts.get(imageId).countings = [];
    this.deleteCountingsFromDb(imageId).subscribe(() => {
      this.countingsUpdated.next({
        imageId: imageId,
        counts: this.imageCounts.get(imageId),
      });
      return this.imageCounts.get(imageId);
    });
  }

  getCountingsForImage(imageId: number): ImageCounts {
    if (imageId > 0 && !this.imageCounts.has(imageId)) {
      this.getCountingsFromDb(imageId).subscribe((data) => {
        if (data && data?.countings?.length) {
          this.imageCounts.set(imageId, data);
          this.countingsUpdated.next({
            imageId: imageId,
            counts: data,
          });
        } else {
          const data = this.createCountingForImage(imageId);
          this.countingsUpdated.next({
            imageId: imageId,
            counts: data,
          });
          return data;
        }
      });
      const data = this.createCountingForImage(imageId);
      this.countingsUpdated.next({
        imageId: imageId,
        counts: data,
      });
      return data;
    } else {
      const data = this.imageCounts.get(imageId);
      this.countingsUpdated.next({
        imageId: imageId,
        counts: data,
      });
      return data;
    }
  }

  addPoint(imageId: number, point: Point) {
    if (!this.imageCounts.has(imageId)) return;
    const activeCountings = this.imageCounts
      .get(imageId)
      .countings.filter((c: any) => c.active);
    if (activeCountings.length == 0) return;
    activeCountings[0].points.push(point);
    this.syncData(imageId).subscribe();
  }

  setActive(imageId: number, idx: number) {
    if (!this.imageCounts.has(imageId)) return;
    const numCountings = this.imageCounts.get(imageId).countings.length;
    for (let i = 0; i < numCountings; i++) {
      if (i == idx) {
        this.imageCounts.get(imageId).countings[i].active = true;
      } else {
        this.imageCounts.get(imageId).countings[i].active = false;
      }
    }
    this.syncData(imageId).subscribe();
  }

  decrementCounting(imageId: number, idx: number) {
    if (!this.imageCounts.has(imageId)) return;
    if (this.imageCounts.get(imageId).countings.length <= idx) return;
    const numPoints =
      this.imageCounts.get(imageId).countings[idx].points.length;
    this.imageCounts.get(imageId).countings[idx].points = this.imageCounts
      .get(imageId)
      .countings[idx].points.slice(0, numPoints - 1);
    this.syncData(imageId).subscribe();
  }

  setColor(imageId: number, idx: number, color: string) {
    if (!this.imageCounts.has(imageId)) return;
    if (this.imageCounts.get(imageId).countings.length <= idx) return;
    this.imageCounts.get(imageId).countings[idx].color = color;
    this.syncData(imageId).subscribe();
  }

  toggleCounting(imageId: number, idx: number) {
    if (!this.imageCounts.has(imageId)) return;
    if (this.imageCounts.get(imageId).countings.length <= idx) return;
    this.imageCounts.get(imageId).countings[idx].visible =
      !this.imageCounts.get(imageId).countings[idx].visible;
    this.syncData(imageId).subscribe();
  }

  setName(imageId: number, idx: number, name: string) {
    if (!this.imageCounts.has(imageId)) return;
    if (this.imageCounts.get(imageId).countings.length <= idx) return;
    this.imageCounts.get(imageId).countings[idx].name = name;
    this.syncData(imageId).subscribe();
  }

  removeCounting(imageId: number, idx: number) {
    if (!this.imageCounts.has(imageId)) return;
    if (this.imageCounts.get(imageId).countings.length <= idx) return;
    this.imageCounts.get(imageId).countings = this.imageCounts
      .get(imageId)
      .countings.filter((elem: any, index: number) => index != idx);
    this.syncData(imageId).subscribe();
  }

  syncData(imageId: number): Observable<any> {
    return this.saveCountingsToDb(imageId, this.imageCounts.get(imageId)).pipe(
      map(() => {
        this.countingsUpdated.next({
          imageId: imageId,
          counts: this.imageCounts.get(imageId),
        });
        return this.imageCounts.get(imageId);
      }),
    );
  }

  saveCountingsToDb(imageId: number, countings: any): Observable<any> {
    const url = `${environment.examInfoBackend}/image_countings/${imageId}`;
    return this.httpClient.post<any>(url, countings);
  }

  getCountingsFromDb(imageId: number): Observable<any> {
    const url = `${environment.examInfoBackend}/image_countings/${imageId}`;
    return this.httpClient.get<any>(url);
  }

  deleteCountingsFromDb(imageId: number): Observable<any> {
    const url = `${environment.examInfoBackend}/image_countings/${imageId}`;
    return this.httpClient.delete<any>(url);
  }
}
